// Middle pane: the thread (bubbles, status lines, notes and events), the opt-out banner, the
// composer. Right pane: the contact panel. Older messages load when you scroll to the top.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ConversationEvent, type Message, type MeResponse, type TimelineItem } from '../../api';
import { useUsers } from '../../hooks/useMe';
import { Loading, ErrorBox, InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { dayLabel, displayName, fmtTime } from '../../lib/format';
import { effectiveTimezone } from '../../lib/timezone';
import { eventWords, outboundStatus } from '../../lib/words';
import { Composer } from './Composer';
import { ContactPanel } from './ContactPanel';

export function Thread({
  conversationId,
  me,
  showContact,
  onBack,
  onToggleContact,
}: {
  conversationId: string;
  me: MeResponse;
  showContact: boolean;
  onBack: () => void;
  onToggleContact: () => void;
}) {
  const qc = useQueryClient();
  const users = useUsers();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  // a rendered template from the New text dialog; taken once, then the history state is cleared
  const [prefill] = useState<string | null>(() => ((location.state as { prefill?: string } | null)?.prefill ?? null));
  useEffect(() => {
    if ((location.state as { prefill?: string } | null)?.prefill) navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = useInfiniteQuery({
    queryKey: ['conversation', conversationId],
    queryFn: ({ pageParam }) => api.conversations.get(conversationId, pageParam),
    initialPageParam: null as string | number | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const head = q.data?.pages[0];
  const timeline = useMemo(() => {
    const seen = new Set<string>();
    const out: TimelineItem[] = [];
    for (const page of q.data?.pages ?? []) {
      for (const t of page.timeline) {
        const key = t.type === 'message' ? `m:${t.message.id}` : `e:${t.event.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(t);
      }
    }
    out.sort((a, b) => seqOf(a) - seqOf(b));
    return out;
  }, [q.data]);

  const maxSeq = timeline.length ? seqOf(timeline[timeline.length - 1]) : 0;
  const hasInbound = !!head?.conversation.last_inbound_at || timeline.some((t) => t.type === 'message' && t.message.direction === 'in');
  const tz = effectiveTimezone(head?.contact ?? null, me.workspace.timezone);

  // ----- mark read when visible -----
  const lastRead = useRef(0);
  useEffect(() => {
    const post = () => {
      if (document.visibilityState !== 'visible') return;
      if (maxSeq <= lastRead.current) return;
      lastRead.current = maxSeq;
      api.conversations
        .markRead(conversationId, maxSeq)
        .then(() => {
          qc.invalidateQueries({ queryKey: ['counts'] });
          qc.invalidateQueries({ queryKey: ['conversations'] });
          qc.invalidateQueries({ queryKey: ['mine-unread'] });
        })
        .catch(() => {
          lastRead.current = 0; // try again on the next change
        });
    };
    post();
    document.addEventListener('visibilitychange', post);
    return () => document.removeEventListener('visibilitychange', post);
  }, [conversationId, maxSeq, qc]);

  // ----- scrolling: stick to the bottom, load older at the top -----
  const scroller = useRef<HTMLDivElement | null>(null);
  const stickBottom = useRef(true);
  const prevHeight = useRef(0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (prevHeight.current && el.scrollHeight > prevHeight.current && !stickBottom.current) {
      // older page arrived: keep what the person was looking at in place
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if (stickBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevHeight.current = el.scrollHeight;
  }, [timeline]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    if (el.scrollTop < 40 && q.hasNextPage && !q.isFetchingNextPage) {
      prevHeight.current = el.scrollHeight;
      q.fetchNextPage();
    }
  }, [q]);

  // ----- actions -----
  const suspected = useMutation({
    mutationFn: (action: 'confirm' | 'dismiss') => api.conversations.optOutSuspected(conversationId, action),
    onSuccess: (_d, action) => {
      qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['scheduled'] });
      toast({ text: action === 'confirm' ? 'Number blocked.' : 'Marked as not an opt-out.', tone: 'ok' });
    },
  });

  const resend = useMutation({
    mutationFn: (m: Message) => api.conversations.resend(conversationId, m.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['conversation', conversationId] }),
    onError: (e) => toast({ text: `Could not resend: ${(e as Error).message}`, tone: 'error' }),
  });

  if (q.isPending) {
    return (
      <>
        <div className="pane thread-pane">
          <Loading label="Opening conversation" />
        </div>
        <div className="pane contact" />
      </>
    );
  }
  if (q.isError || !head) {
    return (
      <>
        <div className="pane thread-pane">
          <div style={{ padding: 16 }}>
            <ErrorBox error={q.error ?? new Error('Conversation not found')} retry={() => q.refetch()} />
          </div>
        </div>
        <div className="pane contact" />
      </>
    );
  }

  const { conversation, contact, opted_out, opt_out } = head;
  const showBanner = conversation.opt_out_suspected && !opted_out;

  return (
    <>
      <div className="pane thread-pane">
        <div className="pane-head">
          <button className="btn ghost small mobile-only" onClick={onBack} aria-label="Back to list">
            ← Back
          </button>
          <div className="grow" style={{ minWidth: 0 }}>
            <strong>{displayName(contact)}</strong>
            {contact.name && <span className="muted small"> · {displayName({ phone_number: contact.phone_number })}</span>}
            {conversation.status === 'closed' && <span className="chip" style={{ marginLeft: 8 }}>Closed</span>}
            {opted_out && <span className="chip danger" style={{ marginLeft: 8 }}>OPTED OUT</span>}
          </div>
          <button className="btn ghost small mobile-only" onClick={onToggleContact}>
            {showContact ? 'Thread' : 'Details'}
          </button>
        </div>

        <div className="thread" ref={scroller} onScroll={onScroll}>
          {q.isFetchingNextPage && <Loading label="Loading older" />}
          {!q.hasNextPage && timeline.length > 0 && <div className="day-sep">Start of conversation</div>}
          {timeline.length === 0 && <div className="empty">No messages yet. Say hello below.</div>}
          {timeline.map((t, i) => {
            const prev = i > 0 ? timeline[i - 1] : null;
            const day = dayLabel(timeAt(t), me.workspace.timezone);
            const prevDay = prev ? dayLabel(timeAt(prev), me.workspace.timezone) : null;
            return (
              <div key={t.type === 'message' ? t.message.id : t.event.id} style={{ display: 'contents' }}>
                {day !== prevDay && <div className="day-sep">{day}</div>}
                {t.type === 'message' ? (
                  <Bubble m={t.message} tz={me.workspace.timezone} contactTz={tz.tz} onResend={() => resend.mutate(t.message)} resending={resend.isPending} />
                ) : (
                  <EventRow e={t.event} tz={me.workspace.timezone} users={users} />
                )}
              </div>
            );
          })}
        </div>

        {showBanner && (
          <div className="notice warn" style={{ margin: '0 12px 8px', borderRadius: 8 }}>
            <div className="row between wrap">
              <span>This looks like an opt-out request.</span>
              <span className="row">
                <button className="btn small danger" disabled={suspected.isPending} onClick={() => suspected.mutate('confirm')}>
                  Block this number
                </button>
                <button className="btn small" disabled={suspected.isPending} onClick={() => suspected.mutate('dismiss')}>
                  Not an opt-out
                </button>
              </span>
            </div>
            <InlineError error={suspected.error} />
          </div>
        )}

        <Composer conversationId={conversationId} me={me} contact={contact} optedOut={opted_out} optOut={opt_out ?? null} contactTz={tz.tz} hasInbound={hasInbound} prefill={prefill} />
      </div>
      <div className="pane contact">
        {/* on a phone the details fill the screen, so they need their own way back to the text */}
        <div className="pane-head contact-head">
          <button className="btn ghost small" onClick={onToggleContact} aria-label="Back to the text">
            ← Back to the text
          </button>
        </div>
        <ContactPanel conversation={conversation} contact={contact} optedOut={opted_out} optOut={opt_out ?? null} me={me} users={users} />
      </div>
    </>
  );
}

function seqOf(t: TimelineItem): number {
  return t.type === 'message' ? Number(t.message.seq ?? 0) : Number(t.event.seq ?? 0);
}
function timeAt(t: TimelineItem): string {
  return t.type === 'message' ? t.message.created_at : t.event.created_at;
}

function Bubble({ m, tz, contactTz, onResend, resending }: { m: Message; tz: string; contactTz: string; onResend: () => void; resending: boolean }) {
  // system rows (blocked reminders, notices) sit in the middle like events
  if (m.kind === 'system') {
    return (
      <div className="sys-row notice" title={fmtTime(m.created_at, tz)}>
        {m.status === 'blocked' ? `${m.body} — ${outboundStatus(m, contactTz, tz).text}` : m.body}
      </div>
    );
  }
  const isIn = m.direction === 'in';
  if (isIn) {
    return (
      <div className="bubble-row in">
        <div className="bubble">
          {m.body}
          <Attachments list={m.attachments} />
          {m.kind === 'self_test' && <div className="tiny muted">self-test reply</div>}
        </div>
        <div className="status-line">{fmtTime(m.received_at ?? m.created_at, tz)}</div>
      </div>
    );
  }
  const st = outboundStatus(m, contactTz, tz);
  const blocked = m.status === 'blocked';
  return (
    <div className={`bubble-row out ${blocked ? 'blocked' : ''}`}>
      <div className="bubble">
        {m.body}
        <Attachments list={m.attachments} />
      </div>
      <div className={`status-line ${st.tone === 'danger' ? 'danger' : st.tone === 'muted' ? 'muted' : ''}`} title={st.tooltip}>
        <span>{fmtTime(m.created_at, tz)}</span>
        <span>·</span>
        <span>{st.text}</span>
        {m.sent_by_user?.name && <span>· {m.sent_by_user.name}</span>}
        {m.ai_suggestion_id && <span className="ai-label" title="Written with the AI helper, sent by a person">AI-assisted</span>}
        {m.kind === 'scheduled' && <span>· reminder</span>}
        {(m.segments ?? 0) >= 2 && <span className={(m.segments ?? 0) >= 3 ? 'danger-text' : 'warn-text'}>· {m.segments} segments</span>}
        {(m.kind === 'opt_out_confirmation' || m.kind === 'opt_in_confirmation') && <span>· automatic</span>}
        {m.override_reason && <span>· sent during quiet hours: {m.override_reason}</span>}
        {st.canResend && (
          <button className="btn link small" onClick={onResend} disabled={resending}>
            Resend
          </button>
        )}
      </div>
    </div>
  );
}

/** Pictures inline (click opens the full size), anything else as a link. */
function Attachments({ list }: { list: Message['attachments'] | null | undefined }) {
  if (!list || list.length === 0) return null;
  return (
    <div className="attach">
      {list.map((a, i) => {
        const url = a.url;
        const image = a.contentType ? a.contentType.startsWith('image/') : /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url) || /\/public\/assets\//.test(url);
        return (
          <a key={i} href={url} target="_blank" rel="noreferrer" title="Open">
            {image ? <img src={url} alt="picture" loading="lazy" /> : `[attachment${a.contentType ? `: ${a.contentType}` : ''}]`}
          </a>
        );
      })}
    </div>
  );
}

function EventRow({ e, tz, users }: { e: ConversationEvent; tz: string; users: { id: string; name: string }[] }) {
  if (e.type === 'note') {
    const by = e.user?.name ?? users.find((u) => u.id === e.user_id)?.name ?? 'Note';
    return (
      <div className="note-card">
        <div className="by">
          {by} · {fmtTime(e.created_at, tz)}
        </div>
        {e.body}
      </div>
    );
  }
  const words = eventWords(e, users);
  const note = e.type === 'assigned' && e.body ? ` — "${e.body}"` : '';
  return (
    <div className="sys-row" title={fmtTime(e.created_at, tz)}>
      {words}
      {note} <span className="faint">{fmtTime(e.created_at, tz)}</span>
    </div>
  );
}
